import React from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  InputAdornment,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Backdrop,
  Button,
  Divider,
  Menu,
  MenuItem,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { KeyboardArrowDown, Search, Close, DragIndicator, MoreVert, Delete } from '@mui/icons-material';
import EditIcon from '../components/icons/EditIcon';
import AddContractForm from '../components/contracts/AddContractForm';
import contractPendingSignatureService, { ContractPendingSignature } from '../services/contractPendingSignatureService';
import contractTemplateService, { ContractTemplate } from '../services/contractTemplateService';
import { useNotification } from '../components/layouts/Layout';
import ResponsiblePersonDisplay from '../components/projects/ResponsiblePersonDisplay';

// Zwiększ rozdzielczość zdjęcia profilowego Google (lh3.googleusercontent) do docelowego rozmiaru
function getHiResGoogleAvatar(rawUrl: string | undefined | null, targetSize = 64): string | null {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  try {
    let url = rawUrl;
    // Warianty: ...=s96-c, =s96, =w120-h120-..., ?sz=50
    url = url.replace(/=s(\d+)/, (_m, _d) => `=s${targetSize}`);
    url = url.replace(/([?&]sz=)(\d+)/, (_m, p1) => `${p1}${targetSize}`);
    url = url.replace(/=w(\d+)-h(\d+)/, () => `=w${targetSize}-h${targetSize}`);
    return url;
  } catch {
    return rawUrl;
  }
}

const headerTabs = ['Wzory umów i protokołów', 'Umowy do podpisu', 'Kontrakty budowlane', 'Gospodarka własna'];

// Kolumny dla umów z zamawiającym
const ALL_COLUMNS_CLIENT = ['contractNumber', 'project', 'date', 'value', 'status', 'actions'];
const COLUMN_LABELS_CLIENT: Record<string, string> = {
  contractNumber: 'Numer umowy',
  project: 'Projekt',
  date: 'Data zawarcia',
  value: 'Wartość',
  status: 'Status',
  actions: 'Akcje'
};

// Kolumny dla umów z podwykonawcami
const ALL_COLUMNS_SUBCONTRACTOR = ['contractNumber', 'project', 'subcontractor', 'date', 'value', 'status'];
const COLUMN_LABELS_SUBCONTRACTOR: Record<string, string> = {
  contractNumber: 'Numer umowy',
  project: 'Projekt',
  subcontractor: 'Podwykonawca',
  date: 'Data zawarcia',
  value: 'Wartość',
  status: 'Status'
};

const STORAGE_KEY = 'contractsTable';
const VISIBLE_KEY_CLIENT = `${STORAGE_KEY}:client:visible`;
const ORDER_KEY_CLIENT = `${STORAGE_KEY}:client:order`;
const VISIBLE_KEY_SUBCONTRACTOR = `${STORAGE_KEY}:subcontractor:visible`;
const ORDER_KEY_SUBCONTRACTOR = `${STORAGE_KEY}:subcontractor:order`;

// Kolumny dla gospodarki własnej (wszystkie zakładki mają te same kolumny)
const ALL_COLUMNS_INTERNAL = ['contractNumber', 'party', 'date', 'value', 'status', 'actions'];
const COLUMN_LABELS_INTERNAL: Record<string, string> = {
  contractNumber: 'Numer umowy',
  party: 'Strona',
  date: 'Data zawarcia',
  value: 'Wartość',
  status: 'Status',
  actions: 'Akcje'
};

const internalTabs = [
  'Umowy o pracę',
  'Umowy zlecenia',
  'Umowy B2B',
  'Najmy',
  'Leasingi',
  'Ubezpieczenia',
  'Banki',
  'Pozostałe'
];

const getInternalStorageKey = (tabIndex: number) => {
  const tabNames = ['employment', 'commission', 'b2b', 'rent', 'leasing', 'insurance', 'bank', 'other'];
  return {
    visible: `${STORAGE_KEY}:internal:${tabNames[tabIndex]}:visible`,
    order: `${STORAGE_KEY}:internal:${tabNames[tabIndex]}:order`
  };
};

// Kolumny dla wzorów umów (wszystkie zakładki mają te same kolumny)
const ALL_COLUMNS_TEMPLATES = ['createdAt', 'name', 'version', 'description', 'createdBy', 'actions'];
const COLUMN_LABELS_TEMPLATES: Record<string, string> = {
  createdAt: 'Data utworzenia',
  name: 'Nazwa',
  version: 'Wersja',
  description: 'Opis',
  createdBy: 'Utworzył(a)',
  actions: 'Akcje'
};

const templateTabs = [
  'Umowy o podwykonawstwo',
  'Umowy Pracownicze',
  'Regulaminy',
  'Protokoły'
];

const getTemplateStorageKey = (tabIndex: number) => {
  const tabNames = ['subcontracting', 'employment', 'regulations', 'protocols'];
  return {
    visible: `${STORAGE_KEY}:templates:${tabNames[tabIndex]}:visible`,
    order: `${STORAGE_KEY}:templates:${tabNames[tabIndex]}:order`
  };
};

// Kolumny dla umów do podpisu
const ALL_COLUMNS_PENDING_SIGNATURE = ['contractNumber', 'contractDate', 'employeeName', 'description', 'employeeEmail', 'unitrescoName', 'unitrescoEmail', 'status', 'actions'];
const COLUMN_LABELS_PENDING_SIGNATURE: Record<string, string> = {
  contractNumber: 'Nr umowy',
  contractDate: 'Data umowy',
  employeeName: 'Pracownik/kontrahent',
  description: 'Opis',
  employeeEmail: 'E-mail',
  unitrescoName: 'Za Unitresco',
  unitrescoEmail: 'e-mail Unitresco',
  status: 'Status',
  actions: 'Akcje'
};

const VISIBLE_KEY_PENDING_SIGNATURE = `${STORAGE_KEY}:pendingSignature:visible`;
const ORDER_KEY_PENDING_SIGNATURE = `${STORAGE_KEY}:pendingSignature:order`;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contracts-tabpanel-${index}`}
      aria-labelledby={`contracts-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 0 }}>{children}</Box>}
    </div>
  );
}

const ContractsPage: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [tab, setTab] = React.useState(0);
  const [searchText, setSearchText] = React.useState('');
  const [searchTextInternal, setSearchTextInternal] = React.useState('');
  const [searchTextTemplates, setSearchTextTemplates] = React.useState('');
  const [searchTextPendingSignature, setSearchTextPendingSignature] = React.useState('');
  const [contractsTab, setContractsTab] = React.useState(0);
  const [internalTab, setInternalTab] = React.useState(0);
  const [templateTab, setTemplateTab] = React.useState(0);
  const [pendingSignatureTab, setPendingSignatureTab] = React.useState(0);
  const [addContractOpen, setAddContractOpen] = React.useState(false);
  const [contractsPendingSignature, setContractsPendingSignature] = React.useState<ContractPendingSignature[]>([]);
  const [loadingContracts, setLoadingContracts] = React.useState(false);
  const [contractTemplates, setContractTemplates] = React.useState<ContractTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = React.useState(false);
  const [templateCounts, setTemplateCounts] = React.useState<Record<string, number>>({
    subcontracting: 0,
    employment: 0,
    regulations: 0,
    protocols: 0
  });
  const [editTemplateOpen, setEditTemplateOpen] = React.useState(false);
  const [editingTemplate, setEditingTemplate] = React.useState<ContractTemplate | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedRowId, setSelectedRowId] = React.useState<string | null>(null);
  const [templateMenuAnchorEl, setTemplateMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<number | null>(null);
  
  // Stany dla zarządzania kolumnami
  const [colsAnchorEl, setColsAnchorEl] = React.useState<null | HTMLElement>(null);
  const colsOpen = Boolean(colsAnchorEl);
  const [colsPosition, setColsPosition] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [colsDragging, setColsDragging] = React.useState<{ startX: number; startY: number; top: number; left: number } | null>(null);
  const [colResizeMode, setColResizeMode] = React.useState<boolean>(false);
  const [dragCol, setDragCol] = React.useState<{ key: string; startX: number; startW: number } | null>(null);
  
  // Szerokości kolumn - osobne dla każdego typu tabeli
  const defaultColumnWidths: Record<string, Record<string, number>> = {
    templates: { createdAt: 150, name: 200, version: 100, description: 300, createdBy: 180, actions: 80 },
    pendingSignature: { contractNumber: 120, contractDate: 120, employeeName: 180, description: 200, employeeEmail: 180, unitrescoName: 150, unitrescoEmail: 180, status: 100, actions: 80 },
    client: { contractNumber: 150, project: 200, date: 120, value: 120, status: 100, actions: 80 },
    subcontractor: { contractNumber: 150, project: 200, subcontractor: 180, date: 120, value: 120, status: 100 },
    internal: { contractNumber: 150, party: 200, date: 120, value: 120, status: 100, actions: 80 }
  };
  
  const getWidthsKey = () => {
    if (tab === 0) {
      const keys = getTemplateStorageKey(templateTab);
      return `${keys.visible}:widths`;
    } else if (tab === 1) {
      return `${VISIBLE_KEY_PENDING_SIGNATURE}:widths`;
    } else if (tab === 2) {
      return contractsTab === 0 ? `${VISIBLE_KEY_CLIENT}:widths` : `${VISIBLE_KEY_SUBCONTRACTOR}:widths`;
    } else {
      const keys = getInternalStorageKey(internalTab);
      return `${keys.visible}:widths`;
    }
  };
  
  const getCurrentWidths = () => {
    const key = getWidthsKey();
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed || {};
      }
    } catch {}
    return {};
  };
  
  const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>(() => getCurrentWidths());
  
  React.useEffect(() => {
    setColumnWidths(getCurrentWidths());
  }, [tab, templateTab, contractsTab, internalTab]);
  
  const getColWidthPx = React.useCallback((keyId: string): string => {
    let defaults: Record<string, number> = {};
    if (tab === 0) defaults = defaultColumnWidths.templates;
    else if (tab === 1) defaults = defaultColumnWidths.pendingSignature;
    else if (tab === 2) defaults = contractsTab === 0 ? defaultColumnWidths.client : defaultColumnWidths.subcontractor;
    else defaults = defaultColumnWidths.internal;
    
    const def = defaults[keyId] ?? 150;
    const cur = columnWidths[keyId];
    const width = typeof cur === 'number' ? Math.max(cur, def) : def;
    return `${width}px`;
  }, [tab, contractsTab, columnWidths]);
  
  // Kolumny dla umów z zamawiającym
  const [columnsVisibleClient, setColumnsVisibleClient] = React.useState<Record<string, boolean>>({});
  const [columnOrderClient, setColumnOrderClient] = React.useState<string[]>(ALL_COLUMNS_CLIENT);
  const [draftColumnsVisibleClient, setDraftColumnsVisibleClient] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrderClient, setDraftOrderClient] = React.useState<string[] | null>(null);
  
  // Kolumny dla umów z podwykonawcami
  const [columnsVisibleSubcontractor, setColumnsVisibleSubcontractor] = React.useState<Record<string, boolean>>({});
  const [columnOrderSubcontractor, setColumnOrderSubcontractor] = React.useState<string[]>(ALL_COLUMNS_SUBCONTRACTOR);
  const [draftColumnsVisibleSubcontractor, setDraftColumnsVisibleSubcontractor] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrderSubcontractor, setDraftOrderSubcontractor] = React.useState<string[] | null>(null);
  
  // Kolumny dla gospodarki własnej - osobne dla każdej zakładki
  const [columnsVisibleInternal, setColumnsVisibleInternal] = React.useState<Record<number, Record<string, boolean>>>({});
  const [columnOrderInternal, setColumnOrderInternal] = React.useState<Record<number, string[]>>({});
  const [draftColumnsVisibleInternal, setDraftColumnsVisibleInternal] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrderInternal, setDraftOrderInternal] = React.useState<string[] | null>(null);
  
  // Kolumny dla wzorów umów - osobne dla każdej zakładki
  const [columnsVisibleTemplates, setColumnsVisibleTemplates] = React.useState<Record<number, Record<string, boolean>>>({});
  const [columnOrderTemplates, setColumnOrderTemplates] = React.useState<Record<number, string[]>>({});
  const [draftColumnsVisibleTemplates, setDraftColumnsVisibleTemplates] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrderTemplates, setDraftOrderTemplates] = React.useState<string[] | null>(null);
  
  // Kolumny dla umów do podpisu
  const [columnsVisiblePendingSignature, setColumnsVisiblePendingSignature] = React.useState<Record<string, boolean>>({});
  const [columnOrderPendingSignature, setColumnOrderPendingSignature] = React.useState<string[]>(ALL_COLUMNS_PENDING_SIGNATURE);
  const [draftColumnsVisiblePendingSignature, setDraftColumnsVisiblePendingSignature] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrderPendingSignature, setDraftOrderPendingSignature] = React.useState<string[] | null>(null);
  
  // Wczytaj ustawienia z localStorage przy montowaniu
  React.useEffect(() => {
    try {
      const visClientRaw = localStorage.getItem(VISIBLE_KEY_CLIENT);
      if (visClientRaw) {
        const vis = JSON.parse(visClientRaw);
        if (vis && typeof vis === 'object') {
          setColumnsVisibleClient(vis);
        }
      }
      const ordClientRaw = localStorage.getItem(ORDER_KEY_CLIENT);
      if (ordClientRaw) {
        const ord = JSON.parse(ordClientRaw);
        if (Array.isArray(ord) && ord.length) {
          const normalized = ord.filter((k: string) => ALL_COLUMNS_CLIENT.includes(k))
            .concat(ALL_COLUMNS_CLIENT.filter(k => !ord.includes(k)));
          setColumnOrderClient(normalized);
        }
      }
      
      const visSubRaw = localStorage.getItem(VISIBLE_KEY_SUBCONTRACTOR);
      if (visSubRaw) {
        const vis = JSON.parse(visSubRaw);
        if (vis && typeof vis === 'object') {
          setColumnsVisibleSubcontractor(vis);
        }
      }
      const ordSubRaw = localStorage.getItem(ORDER_KEY_SUBCONTRACTOR);
      if (ordSubRaw) {
        const ord = JSON.parse(ordSubRaw);
        if (Array.isArray(ord) && ord.length) {
          const normalized = ord.filter((k: string) => ALL_COLUMNS_SUBCONTRACTOR.includes(k))
            .concat(ALL_COLUMNS_SUBCONTRACTOR.filter(k => !ord.includes(k)));
          setColumnOrderSubcontractor(normalized);
        }
      }
      
      // Wczytaj ustawienia dla gospodarki własnej
      const internalVisible: Record<number, Record<string, boolean>> = {};
      const internalOrder: Record<number, string[]> = {};
      for (let i = 0; i < internalTabs.length; i++) {
        const keys = getInternalStorageKey(i);
        try {
          const visRaw = localStorage.getItem(keys.visible);
          if (visRaw) {
            const vis = JSON.parse(visRaw);
            if (vis && typeof vis === 'object') {
              internalVisible[i] = vis;
            }
          }
          const ordRaw = localStorage.getItem(keys.order);
          if (ordRaw) {
            const ord = JSON.parse(ordRaw);
            if (Array.isArray(ord) && ord.length) {
              const normalized = ord.filter((k: string) => ALL_COLUMNS_INTERNAL.includes(k))
                .concat(ALL_COLUMNS_INTERNAL.filter(k => !ord.includes(k)));
              internalOrder[i] = normalized;
            } else {
              internalOrder[i] = ALL_COLUMNS_INTERNAL;
            }
          } else {
            internalOrder[i] = ALL_COLUMNS_INTERNAL;
          }
        } catch {}
      }
      if (Object.keys(internalVisible).length > 0) setColumnsVisibleInternal(internalVisible);
      if (Object.keys(internalOrder).length > 0) setColumnOrderInternal(internalOrder);
      
      // Wczytaj ustawienia dla wzorów umów
      const templatesVisible: Record<number, Record<string, boolean>> = {};
      const templatesOrder: Record<number, string[]> = {};
      for (let i = 0; i < templateTabs.length; i++) {
        const keys = getTemplateStorageKey(i);
        try {
          const visRaw = localStorage.getItem(keys.visible);
          if (visRaw) {
            const vis = JSON.parse(visRaw);
            if (vis && typeof vis === 'object') {
              templatesVisible[i] = vis;
            }
          }
          const ordRaw = localStorage.getItem(keys.order);
          if (ordRaw) {
            const ord = JSON.parse(ordRaw);
            if (Array.isArray(ord) && ord.length) {
              const normalized = ord.filter((k: string) => ALL_COLUMNS_TEMPLATES.includes(k))
                .concat(ALL_COLUMNS_TEMPLATES.filter(k => !ord.includes(k)));
              templatesOrder[i] = normalized;
            } else {
              templatesOrder[i] = ALL_COLUMNS_TEMPLATES;
            }
          } else {
            templatesOrder[i] = ALL_COLUMNS_TEMPLATES;
          }
        } catch {}
      }
      if (Object.keys(templatesVisible).length > 0) setColumnsVisibleTemplates(templatesVisible);
      if (Object.keys(templatesOrder).length > 0) setColumnOrderTemplates(templatesOrder);
    } catch {}
  }, []);
  
  // Obsługa przeciągania okna "Dostosuj kolumny"
  React.useEffect(() => {
    if (!colsDragging) return;
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - colsDragging.startX;
      const deltaY = e.clientY - colsDragging.startY;
      setColsPosition({
        top: Math.max(0, Math.min(window.innerHeight - 300, colsDragging.top + deltaY)),
        left: Math.max(0, Math.min(window.innerWidth - 280, colsDragging.left + deltaX))
      });
    };
    const handleMouseUp = () => {
      setColsDragging(null);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [colsDragging]);
  
  // ESC wychodzi z trybu dostosowania kolumn (zamyka okno i linie pomocnicze)
  React.useEffect(() => {
    if (!(colResizeMode || colsOpen)) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        const cols = getCurrentColumns();
        setDragCol(null);
        setColResizeMode(false);
        setColsAnchorEl(null);
        cols.setDraftVisible(null);
        cols.setDraftOrder(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [colResizeMode, colsOpen, tab, templateTab, contractsTab, internalTab, draftColumnsVisibleTemplates, draftOrderTemplates, draftColumnsVisiblePendingSignature, draftOrderPendingSignature, draftColumnsVisibleClient, draftOrderClient, draftColumnsVisibleSubcontractor, draftOrderSubcontractor, draftColumnsVisibleInternal, draftOrderInternal]);
  
  // Obsługa przeciągania do zmiany szerokości kolumn
  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      e.preventDefault();
      const dx = e.clientX - dragCol.startX;
      const newW = Math.max(24, dragCol.startW + dx);
      setColumnWidths(prev => ({ ...prev, [dragCol.key]: newW }));
    };
    const onUp = (e: MouseEvent) => {
      e.preventDefault();
      setDragCol(null);
      const key = getWidthsKey();
      try {
        const payload = JSON.stringify(columnWidths);
        localStorage.setItem(key, payload);
      } catch {}
    };
    if (dragCol) {
      try { document.body.style.cursor = 'col-resize'; } catch {}
      window.addEventListener('mousemove', onMove, { passive: false } as any);
      window.addEventListener('mouseup', onUp as any);
    }
    return () => {
      window.removeEventListener('mousemove', onMove as any);
      window.removeEventListener('mouseup', onUp as any);
      try { document.body.style.cursor = ''; } catch {}
    };
  }, [dragCol, columnWidths]);
  
  // Zapisz szerokości kolumn
  React.useEffect(() => {
    if (!dragCol) {
      const key = getWidthsKey();
      try {
        const payload = JSON.stringify(columnWidths);
        localStorage.setItem(key, payload);
      } catch {}
    }
  }, [columnWidths, dragCol]);
  
  // Funkcja renderująca nagłówek kolumny z możliwością zmiany szerokości
  const renderResizableTh = (keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center') => (
    <TableCell
      align={align}
      sx={{
        width: getColWidthPx(keyId),
        maxWidth: 'none',
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: '#FAFAFA',
        borderRight: '1px solid #d4d4d9',
        borderRadius: 0,
        boxShadow: 'none',
        userSelect: 'none',
        padding: '1px 4px',
        fontSize: '0.85rem',
        fontFamily: 'Roboto, sans-serif'
      }}
    >
      {node}
      {(colResizeMode) && (
        <Box
          role="separator"
          aria-orientation="vertical"
          onMouseDown={(e) => { 
            e.preventDefault(); 
            e.stopPropagation(); 
            setDragCol({ 
              key: keyId, 
              startX: e.clientX, 
              startW: parseInt(getColWidthPx(keyId)) || (columnWidths[keyId] ?? defaultColumnWidths.templates[keyId] ?? 150) 
            }); 
          }}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '12px',
            minHeight: '100%',
            cursor: 'col-resize',
            zIndex: 15,
            touchAction: 'none',
            pointerEvents: 'auto',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '50%',
              top: 0,
              bottom: 0,
              width: '2px',
              transform: 'translateX(-50%)',
              backgroundColor: '#FF0000',
              zIndex: 16
            },
            '&:hover': { 
              backgroundColor: 'rgba(0,0,0,0.06)',
              '&::before': {
                width: '3px',
                backgroundColor: '#CC0000'
              }
            }
          }}
        />
      )}
    </TableCell>
  );
  
  // Funkcje pomocnicze do renderowania kolumn
  const getCurrentColumns = () => {
    if (tab === 0) {
      // Wzory umów i protokołów
      const keys = getTemplateStorageKey(templateTab);
      return {
        all: ALL_COLUMNS_TEMPLATES,
        labels: COLUMN_LABELS_TEMPLATES,
        visible: columnsVisibleTemplates[templateTab] || {},
        order: columnOrderTemplates[templateTab] || ALL_COLUMNS_TEMPLATES,
        draftVisible: draftColumnsVisibleTemplates,
        draftOrder: draftOrderTemplates,
        setDraftVisible: setDraftColumnsVisibleTemplates,
        setDraftOrder: setDraftOrderTemplates,
        visibleKey: keys.visible,
        orderKey: keys.order,
        setVisible: (vis: Record<string, boolean>) => {
          setColumnsVisibleTemplates(prev => ({ ...prev, [templateTab]: vis }));
        },
        setOrder: (ord: string[]) => {
          setColumnOrderTemplates(prev => ({ ...prev, [templateTab]: ord }));
        }
      };
    } else if (tab === 1) {
      // Umowy do podpisu
      return {
        all: ALL_COLUMNS_PENDING_SIGNATURE,
        labels: COLUMN_LABELS_PENDING_SIGNATURE,
        visible: columnsVisiblePendingSignature,
        order: columnOrderPendingSignature,
        draftVisible: draftColumnsVisiblePendingSignature,
        draftOrder: draftOrderPendingSignature,
        setDraftVisible: setDraftColumnsVisiblePendingSignature,
        setDraftOrder: setDraftOrderPendingSignature,
        visibleKey: VISIBLE_KEY_PENDING_SIGNATURE,
        orderKey: ORDER_KEY_PENDING_SIGNATURE,
        setVisible: setColumnsVisiblePendingSignature,
        setOrder: setColumnOrderPendingSignature
      };
    } else if (tab === 2) {
      // Kontrakty budowlane
      return contractsTab === 0 ? {
        all: ALL_COLUMNS_CLIENT,
        labels: COLUMN_LABELS_CLIENT,
        visible: columnsVisibleClient,
        order: columnOrderClient,
        draftVisible: draftColumnsVisibleClient,
        draftOrder: draftOrderClient,
        setDraftVisible: setDraftColumnsVisibleClient,
        setDraftOrder: setDraftOrderClient,
        visibleKey: VISIBLE_KEY_CLIENT,
        orderKey: ORDER_KEY_CLIENT,
        setVisible: setColumnsVisibleClient,
        setOrder: setColumnOrderClient
      } : {
        all: ALL_COLUMNS_SUBCONTRACTOR,
        labels: COLUMN_LABELS_SUBCONTRACTOR,
        visible: columnsVisibleSubcontractor,
        order: columnOrderSubcontractor,
        draftVisible: draftColumnsVisibleSubcontractor,
        draftOrder: draftOrderSubcontractor,
        setDraftVisible: setDraftColumnsVisibleSubcontractor,
        setDraftOrder: setDraftOrderSubcontractor,
        visibleKey: VISIBLE_KEY_SUBCONTRACTOR,
        orderKey: ORDER_KEY_SUBCONTRACTOR,
        setVisible: setColumnsVisibleSubcontractor,
        setOrder: setColumnOrderSubcontractor
      };
    } else {
      // Gospodarka własna
      const keys = getInternalStorageKey(internalTab);
      return {
        all: ALL_COLUMNS_INTERNAL,
        labels: COLUMN_LABELS_INTERNAL,
        visible: columnsVisibleInternal[internalTab] || {},
        order: columnOrderInternal[internalTab] || ALL_COLUMNS_INTERNAL,
        draftVisible: draftColumnsVisibleInternal,
        draftOrder: draftOrderInternal,
        setDraftVisible: setDraftColumnsVisibleInternal,
        setDraftOrder: setDraftOrderInternal,
        visibleKey: keys.visible,
        orderKey: keys.order,
        setVisible: (vis: Record<string, boolean>) => {
          setColumnsVisibleInternal(prev => ({ ...prev, [internalTab]: vis }));
        },
        setOrder: (ord: string[]) => {
          setColumnOrderInternal(prev => ({ ...prev, [internalTab]: ord }));
        }
      };
    }
  };
  
  const isColumnVisible = (key: string) => {
    const cols = getCurrentColumns();
    return cols.visible[key] !== false;
  };
  
  const getVisibleColumns = () => {
    const cols = getCurrentColumns();
    return cols.order.filter(key => isColumnVisible(key));
  };

  // Wczytaj umowy do podpisu
  const loadContractsPendingSignature = React.useCallback(async () => {
    try {
      setLoadingContracts(true);
      const statusMap: Record<number, string | undefined> = {
        0: undefined, // Wszystkie
        1: 'Oczekujące',
        2: 'Podpisane'
      };
      const status = statusMap[pendingSignatureTab];
      const contracts = await contractPendingSignatureService.getAllContracts(status, searchTextPendingSignature || undefined);
      setContractsPendingSignature(contracts);
    } catch (error) {
      console.error('Błąd ładowania umów:', error);
    } finally {
      setLoadingContracts(false);
    }
  }, [pendingSignatureTab, searchTextPendingSignature]);

  React.useEffect(() => {
    if (tab === 1) {
      // Umowy do podpisu (nowy index)
      loadContractsPendingSignature();
    }
  }, [tab, loadContractsPendingSignature]);

  // Wczytaj wzory umów
  // Funkcja do ładowania liczników dla wszystkich kategorii
  const loadTemplateCounts = React.useCallback(async () => {
    try {
      // Załaduj wszystkie wzory umów bez filtrowania po kategorii
      const allTemplates = await contractTemplateService.getAllTemplates(undefined, searchTextTemplates || undefined);
      
      // Policz wzory dla każdej kategorii
      const counts: Record<string, number> = {
        subcontracting: 0,
        employment: 0,
        regulations: 0,
        protocols: 0
      };
      
      allTemplates.forEach(template => {
        if (template.category && counts.hasOwnProperty(template.category)) {
          counts[template.category]++;
        }
      });
      
      setTemplateCounts(counts);
    } catch (error) {
      console.error('Błąd ładowania liczników wzorów umów:', error);
    }
  }, [searchTextTemplates]);

  const loadContractTemplates = React.useCallback(async () => {
    try {
      setLoadingTemplates(true);
      const categoryMap: Record<number, string> = {
        0: 'subcontracting',
        1: 'employment',
        2: 'regulations',
        3: 'protocols'
      };
      const category = categoryMap[templateTab];
      const templates = await contractTemplateService.getAllTemplates(category, searchTextTemplates || undefined);
      setContractTemplates(templates);
      
      // Zaktualizuj liczniki po załadowaniu wzorów
      await loadTemplateCounts();
    } catch (error) {
      console.error('Błąd ładowania wzorów umów:', error);
      showNotification('Błąd ładowania wzorów umów', 'error');
    } finally {
      setLoadingTemplates(false);
    }
  }, [templateTab, searchTextTemplates, showNotification, loadTemplateCounts]);

  React.useEffect(() => {
    if (tab === 0) {
      // Wzory umów i protokołów (nowy index)
      loadContractTemplates();
      // Załaduj liczniki przy pierwszym otwarciu zakładki
      loadTemplateCounts();
    }
  }, [tab, templateTab, searchTextTemplates, loadContractTemplates, loadTemplateCounts]);

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px', position: 'relative' }}>
      {/* Wróć do aplikacji – pozycja jak na stronie szczegółów projektu */}
      <Box
        onClick={() => navigate('/app')}
        sx={{
          position: 'absolute',
          top: '-24px',
          left: 0,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        aria-label="Wróć do aplikacji"
        role="button"
      >
        <KeyboardArrowDown sx={{ transform: 'rotate(90deg)', width: 16, height: 16, color: '#1F1FB0' }} />
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            lineHeight: '16px',
            fontWeight: 500,
            color: '#1F1FB0',
          }}
        >
          Wróć do aplikacji
        </Typography>
      </Box>

      {/* Nagłówek w tej samej pozycji co nazwa projektu na stronie /projects/:id */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 0, mb: 2 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 400,
            color: '#020242',
            m: 0,
            mt: '8.5px'
          }}
        >
          Umowy
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Button 
            variant="contained" 
            onClick={() => {
              if (tab === 0) {
                // Wzory umów i protokołów - otwórz formularz wzorów
                setEditingTemplate(null);
                setEditTemplateOpen(true);
              } else {
                // Inne zakładki - otwórz formularz umów
                setAddContractOpen(true);
              }
            }}
            data-action="add"
            data-hotkey-add="true"
            sx={{ 
              width: 116,
              height: 40,
              borderRadius: '4px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              backgroundColor: '#4040DB',
              color: '#fff',
              '&:hover': { backgroundColor: '#1F1FB0' },
              '&:active': { backgroundColor: '#1A1A98' }
            }}
          >
            {tab === 0 ? 'Dodaj wzór' : 'Dodaj umowę'}
          </Button>
        </Box>
      </Box>

      {/* Pasek zakładek pod nagłówkiem – styl jak na stronie projektu */}
      <Box
        data-tab-scope="main"
        sx={{ mt: '28px', mb: 2, borderBottom: '1px solid #EDEDED', display: 'flex', gap: 3, alignItems: 'flex-end' }}
      >
        {headerTabs.map((label, idx) => (
          <Box
            key={label}
            data-tab="main"
            aria-selected={tab === idx ? 'true' : 'false'}
            onClick={() => setTab(idx)}
            sx={{
              position: 'relative',
              pb: 1,
              cursor: 'pointer',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '16px',
              color: '#020242',
              fontWeight: tab === idx ? 500 : 400,
              userSelect: 'none',
            }}
          >
            {label}
            {tab === idx && (
              <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, bgcolor: '#000000' }} />
            )}
          </Box>
        ))}
      </Box>

      {/* Filtr "Szukaj umowy" - dla zakładki "Wzory umów i protokołów" */}
      {tab === 0 && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <TextField
            size="small"
            placeholder="Szukaj umowy"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            variant="outlined"
            inputProps={{
              'data-role': 'page-filter',
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  try { (e.currentTarget as HTMLInputElement).blur(); } catch {}
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ width: 20, height: 20, color: '#595966' }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: 300,
              '& .MuiOutlinedInput-root': {
                height: 36,
                backgroundColor: '#fff',
                borderRadius: '4px'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#d4d4d9'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#595966'
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#595966 !important'
              },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
              '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
            }}
          />
        </Box>
      )}

      {/* Filtr "Szukaj umowy" - dla zakładki "Umowy do podpisu" */}
      {tab === 1 && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <TextField
            size="small"
            placeholder="Szukaj umowy"
            value={searchTextInternal}
            onChange={(e) => setSearchTextInternal(e.target.value)}
            variant="outlined"
            inputProps={{
              'data-role': 'page-filter',
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  try { (e.currentTarget as HTMLInputElement).blur(); } catch {}
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ width: 20, height: 20, color: '#595966' }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: 300,
              '& .MuiOutlinedInput-root': {
                height: 36,
                backgroundColor: '#fff',
                borderRadius: '4px'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#d4d4d9'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#595966'
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#595966 !important'
              },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
              '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
            }}
          />
        </Box>
      )}

      {/* Filtr "Szukaj umowy" - dla zakładki "Kontrakty budowlane" */}
      {tab === 2 && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <TextField
            size="small"
            placeholder="Szukaj umowy"
            value={searchTextTemplates}
            onChange={(e) => setSearchTextTemplates(e.target.value)}
            variant="outlined"
            inputProps={{
              'data-role': 'page-filter',
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  try { (e.currentTarget as HTMLInputElement).blur(); } catch {}
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ width: 20, height: 20, color: '#595966' }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: 300,
              '& .MuiOutlinedInput-root': {
                height: 36,
                backgroundColor: '#fff',
                borderRadius: '4px'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#d4d4d9'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#595966'
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#595966 !important'
              },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
              '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
            }}
          />
        </Box>
      )}

      {/* Filtr "Szukaj umowy" - dla zakładki "Gospodarka własna" */}
      {tab === 3 && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <TextField
            size="small"
            placeholder="Szukaj umowy"
            value={searchTextPendingSignature}
            onChange={(e) => setSearchTextPendingSignature(e.target.value)}
            variant="outlined"
            inputProps={{
              'data-role': 'page-filter',
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  try { (e.currentTarget as HTMLInputElement).blur(); } catch {}
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ width: 20, height: 20, color: '#595966' }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: 300,
              '& .MuiOutlinedInput-root': {
                height: 36,
                backgroundColor: '#fff',
                borderRadius: '4px'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#d4d4d9'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#595966'
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#595966 !important'
              },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
              '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
            }}
          />
        </Box>
      )}

      {/* Zakładki i tabela - dla zakładki "Wzory umów i protokołów" */}
      {tab === 0 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
            <Tabs 
              value={templateTab} 
              onChange={(_, v) => setTemplateTab(v)} 
              aria-label="template contracts tabs"
              sx={{ 
                minHeight: 40,
                height: 40,
                flexGrow: 1,
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTabs-flexContainer': { gap: '2px' }
              }}
            >
              {templateTabs.map((label, idx) => {
                const categoryMap: Record<number, string> = {
                  0: 'subcontracting',
                  1: 'employment',
                  2: 'regulations',
                  3: 'protocols'
                };
                const category = categoryMap[idx];
                const count = templateCounts[category] || 0;
                return (
                  <Tab 
                    key={label}
                    disableRipple
                    label={`${label} (${count})`}
                    sx={{
                    textTransform: 'none',
                    color: '#020242',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    px: 3,
                    minHeight: 32,
                    height: 32,
                    mt: '8px',
                    bgcolor: '#F1F1F6',
                    borderRadius: '4px 4px 0 0',
                    '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                    '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                  }}
                />
                );
              })}
            </Tabs>
            <IconButton
              size="small"
              sx={{
                width: 32,
                height: 32,
                borderRadius: '4px',
                mt: '4px',
                p: 0,
                alignSelf: 'flex-end',
                bgcolor: colsOpen && tab === 0 ? '#E0E0F5' : 'transparent', // Wzory umów i protokołów
                transition: 'background-color 0.15s ease',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
              }}
              onClick={(e) => {
                const cols = getCurrentColumns();
                if (cols.draftVisible == null) cols.setDraftVisible({ ...cols.visible });
                if (cols.draftOrder == null) cols.setDraftOrder([...cols.order]);
                setColResizeMode(true);
                setColsAnchorEl(e.currentTarget);
              }}
            >
              <Box
                component="img"
                alt="kolumny"
                src={'/assets/icons/frame-344.svg?v=2'}
                sx={{ width: 32, height: 32 }}
              />
            </IconButton>
          </Box>

          {templateTabs.map((_, idx) => (
            <TabPanel key={idx} value={templateTab} index={idx}>
              <TableContainer 
                component={Paper} 
                sx={{ 
                  height: 432, 
                  overflow: 'auto', 
                  position: 'relative', 
                  border: '1px solid #d0d0d6', 
                  borderBottom: '1px solid #d0d0d6', 
                  borderRadius: 0, 
                  borderTopRightRadius: 4, 
                  borderBottomLeftRadius: 4, 
                  borderBottomRightRadius: 4, 
                  overflowX: 'hidden', 
                  overflowY: 'auto', 
                  boxShadow: 'none', 
                  scrollbarWidth: 'none', 
                  msOverflowStyle: 'none', 
                  '&::-webkit-scrollbar': { width: 0, height: 0 } 
                }}
              >
                <Table 
                  size="small" 
                  stickyHeader 
                  sx={{
                    tableLayout: 'fixed',
                    width: '100%',
                    '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
                    '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
                    '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
                    '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {getVisibleColumns().map((key) => {
                        // Pusty nagłówek dla kolumny "actions" (ikony)
                        if (key === 'actions') {
                          return (
                            renderResizableTh(key, <></>)
                          );
                        }
                        const label = COLUMN_LABELS_TEMPLATES[key];
                        // Renderuj nagłówek - jeśli nie ma labela, zwróć pusty nagłówek
                        return (
                          renderResizableTh(key, label || '')
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingTemplates ? (
                      <TableRow>
                        <TableCell colSpan={getVisibleColumns().length} sx={{ textAlign: 'center', py: 4, color: '#9e9e9e' }}>
                          Ładowanie...
                        </TableCell>
                      </TableRow>
                    ) : contractTemplates.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={getVisibleColumns().length} sx={{ textAlign: 'center', py: 4, color: '#9e9e9e' }}>
                          Brak danych
                        </TableCell>
                      </TableRow>
                    ) : (
                      contractTemplates.map((template) => (
                        <TableRow key={template.id}>
                          {getVisibleColumns().map((key) => {
                            if (key === 'createdAt') {
                              return (
                                <TableCell key={key}>
                                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '0.85rem', color: '#2D2D32' }}>
                                    {new Date(template.created_at).toISOString().split('T')[0]}
                                  </Typography>
                                </TableCell>
                              );
                            }
                            if (key === 'name') {
                              return (
                                <TableCell key={key}>
                                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '0.85rem', color: '#2D2D32' }}>
                                    {template.name}
                                  </Typography>
                                </TableCell>
                              );
                            }
                            if (key === 'version') {
                              return (
                                <TableCell key={key}>
                                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '0.85rem', color: '#2D2D32' }}>
                                    {template.version || '-'}
                                  </Typography>
                                </TableCell>
                              );
                            }
                            if (key === 'description') {
                              return (
                                <TableCell key={key}>
                                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '0.85rem', color: '#2D2D32' }}>
                                    {template.description || '-'}
                                  </Typography>
                                </TableCell>
                              );
                            }
                            if (key === 'createdBy') {
                              return (
                                <TableCell key={key}>
                                  {template.creator ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Tooltip 
                                        title={template.creator.display_name || `${template.creator.first_name || ''} ${template.creator.last_name || ''}`.trim() || template.creator.email || ''} 
                                        arrow 
                                        placement="top"
                                      >
                                        <Avatar 
                                          src={getHiResGoogleAvatar(template.creator.avatar_url, 48) || undefined} 
                                          alt={template.creator.display_name || `${template.creator.first_name || ''} ${template.creator.last_name || ''}`.trim() || template.creator.email || ''}
                                          sx={{ width: 24, height: 24, fontSize: '0.75rem' }}
                                        >
                                          {(() => {
                                            const name = template.creator.display_name || `${template.creator.first_name || ''} ${template.creator.last_name || ''}`.trim() || template.creator.email || '';
                                            if (template.creator.first_name && template.creator.last_name) {
                                              return `${template.creator.first_name.charAt(0)}${template.creator.last_name.charAt(0)}`.toUpperCase();
                                            } else if (template.creator.display_name) {
                                              return template.creator.display_name.charAt(0).toUpperCase();
                                            } else if (template.creator.email) {
                                              return template.creator.email.charAt(0).toUpperCase();
                                            }
                                            return '?';
                                          })()}
                                        </Avatar>
                                      </Tooltip>
                                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '0.85rem', color: '#2D2D32' }}>
                                        {template.creator.display_name || `${template.creator.first_name || ''} ${template.creator.last_name || ''}`.trim() || template.creator.email || '-'}
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '0.85rem', color: '#9e9e9e' }}>-</Typography>
                                  )}
                                </TableCell>
                              );
                            }
                            if (key === 'actions') {
                              const getStatusIcon = (status?: string) => {
                                switch (status) {
                                  case 'W opracowaniu':
                                    return '/assets/icons/pending_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
                                  case 'Do zatwierdzenia':
                                    return '/assets/icons/approval_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
                                  case 'Uwagi':
                                    return '/assets/icons/check.svg';
                                  case 'Odrzucam':
                                    return '/assets/icons/error.svg';
                                  case 'Zatwierdzam':
                                    return '/assets/icons/ok.svg';
                                  default:
                                    return '/assets/icons/pending_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
                                }
                              };
                              
                              const getStatusColor = (status?: string) => {
                                switch (status) {
                                  case 'W opracowaniu':
                                    return '#1976d2'; // Niebieski
                                  case 'Do zatwierdzenia':
                                    return '#3F3FDC';
                                  default:
                                    return undefined;
                                }
                              };
                              
                              const getStatusTitle = (status?: string) => {
                                return status || 'W opracowaniu';
                              };
                              
                              return (
                                <TableCell key={key}>
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        window.open(template.google_doc_url, '_blank');
                                      }}
                                      title="Otwórz dokument"
                                    >
                                      <Box component="img" src="/assets/icons/docs_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg" alt="Google Docs" sx={{ width: 20, height: 20 }} />
                                    </IconButton>
                                    {template.status && (
                                      <Tooltip title={getStatusTitle(template.status)} arrow placement="top">
                                        <Box
                                          component="img"
                                          src={getStatusIcon(template.status)}
                                          alt={getStatusTitle(template.status)}
                                          sx={{ 
                                            width: 20, 
                                            height: 20,
                                            ...(getStatusColor(template.status) ? {
                                              filter: `brightness(0) saturate(100%) ${(() => {
                                                const color = getStatusColor(template.status);
                                                if (color === '#1976d2') {
                                                  // Niebieski - użyj maski koloru
                                                  return 'invert(27%) sepia(95%) saturate(1352%) hue-rotate(195deg) brightness(0.9)';
                                                }
                                                if (color === '#3F3FDC') {
                                                  // #3F3FDC - niebiesko-fioletowy
                                                  return 'invert(25%) sepia(95%) saturate(5000%) hue-rotate(240deg) brightness(0.9)';
                                                }
                                                return '';
                                              })()}`
                                            } : {})
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        setTemplateMenuAnchorEl(e.currentTarget);
                                        setSelectedTemplateId(template.id);
                                      }}
                                      title="Więcej opcji"
                                    >
                                      <MoreVert fontSize="small" sx={{ color: '#595966' }} />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              );
                            }
                            return null;
                          })}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>
          ))}

          {/* Okno "Dostosuj kolumny" dla wzorów umów */}
          <Backdrop 
            open={colsOpen && tab === 0} // Wzory umów i protokołów 
            sx={{ 
              color: '#fff', 
              zIndex: (theme) => theme.zIndex.modal + 1, 
              backgroundColor: 'rgba(0,0,0,0.35)', 
              pointerEvents: 'none' 
            }}
          >
            <Paper
              elevation={8}
              sx={{
                position: 'fixed',
                bottom: colsPosition.top === 0 ? 0 : 'auto',
                top: colsPosition.top > 0 ? colsPosition.top : 'auto',
                right: colsPosition.left === 0 ? 0 : 'auto',
                left: colsPosition.left > 0 ? colsPosition.left : 'auto',
                height: '50vh',
                width: '280px',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
                borderRadius: 0,
                zIndex: (theme) => theme.zIndex.modal + 2,
                backgroundColor: '#fff',
                pointerEvents: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box
                sx={{ 
                  px: 2, 
                  py: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  flex: '0 0 auto', 
                  position: 'sticky', 
                  top: 0, 
                  bgcolor: '#FFFFFF', 
                  zIndex: 1, 
                  cursor: 'move', 
                  userSelect: 'none' 
                }}
                onMouseDown={(e) => {
                  const cols = getCurrentColumns();
                  if (cols.draftVisible == null) cols.setDraftVisible({ ...cols.visible });
                  if (cols.draftOrder == null) cols.setDraftOrder([...cols.order]);
                  setColsDragging({ 
                    startX: e.clientX, 
                    startY: e.clientY, 
                    top: colsPosition.top, 
                    left: colsPosition.left 
                  });
                }}
              >
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>
                  Dostosuj kolumny
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => {
                    const cols = getCurrentColumns();
                    setColsAnchorEl(null);
                    setColResizeMode(false);
                    cols.setDraftVisible(null);
                    cols.setDraftOrder(null);
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
              <Divider />
              <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
                {(() => {
                  const cols = getCurrentColumns();
                  const list = cols.draftVisible ?? cols.visible;
                  if (cols.draftVisible == null) cols.setDraftVisible({ ...cols.visible });
                  const ord = cols.draftOrder ?? cols.order;
                  if (cols.draftOrder == null) cols.setDraftOrder([...cols.order]);
                  return ord.map((key) => (
                    <Box 
                      key={key} 
                      sx={{ py: 1, px: 2 }}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData('text/plain', key); }}
                      onDragOver={(e) => { e.preventDefault(); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const src = e.dataTransfer.getData('text/plain');
                        if (!src || src === key) return;
                        cols.setDraftOrder(prev => {
                          const cur = [...(prev || ord)];
                          const from = cur.indexOf(src);
                          const to = cur.indexOf(key);
                          if (from < 0 || to < 0) return cur;
                          cur.splice(to, 0, cur.splice(from, 1)[0]);
                          return cur;
                        });
                      }}
                    >
                      <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <IconButton 
                            onClick={() => cols.setDraftVisible(prev => ({ ...(prev || {}), [key]: !((prev || list)[key] !== false) }))} 
                            size="small" 
                            sx={{ p: 0 }} 
                            aria-label="toggle column"
                          >
                            <Box 
                              component="img" 
                              src={(cols.draftVisible ?? cols.visible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} 
                              alt="check" 
                              sx={{ width: 23, height: 24 }} 
                            />
                          </IconButton>
                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>
                            {cols.labels[key] || key.toUpperCase()}
                          </Typography>
                        </Box>
                        <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                      </Box>
                    </Box>
                  ));
                })()}
              </Box>
              <Divider />
              <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
                <Button 
                  variant="text" 
                  onClick={() => {
                    const cols = getCurrentColumns();
                    setColsAnchorEl(null);
                    setColResizeMode(false);
                    cols.setDraftVisible(null);
                    cols.setDraftOrder(null);
                  }} 
                  sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}
                >
                  Anuluj
                </Button>
                <Button 
                  variant="contained" 
                  sx={{ 
                    width: 123, 
                    height: 40, 
                    borderRadius: '4px', 
                    textTransform: 'none', 
                    fontFamily: 'Roboto, sans-serif', 
                    fontWeight: 600, 
                    fontSize: 14, 
                    backgroundColor: '#020242', 
                    '&:hover': { backgroundColor: '#020242' } 
                  }} 
                  onClick={() => {
                    const cols = getCurrentColumns();
                    if (cols.draftVisible) {
                      cols.setVisible(cols.draftVisible);
                      try {
                        const payload = JSON.stringify(cols.draftVisible);
                        localStorage.setItem(cols.visibleKey, payload);
                      } catch {}
                    }
                    if (cols.draftOrder) {
                      const normalized = cols.draftOrder
                        .filter((k) => cols.all.includes(k))
                        .concat(cols.all.filter(k => !cols.draftOrder!.includes(k)));
                      cols.setOrder(normalized);
                      try {
                        const payload = JSON.stringify(normalized);
                        localStorage.setItem(cols.orderKey, payload);
                      } catch {}
                    }
                    setColsAnchorEl(null);
                    setColResizeMode(false);
                    cols.setDraftVisible(null);
                    cols.setDraftOrder(null);
                  }}
                >
                  Zapisz zmiany
                </Button>
              </Box>
            </Paper>
          </Backdrop>
        </>
      )}

      {/* Zakładki i tabela - dla zakładki "Umowy do podpisu" */}
      {tab === 1 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
            <Tabs 
              value={pendingSignatureTab} 
              onChange={(_, v) => setPendingSignatureTab(v)} 
              aria-label="pending signature tabs"
              sx={{ 
                minHeight: 40,
                height: 40,
                flexGrow: 1,
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTabs-flexContainer': { gap: '2px' }
              }}
            >
              <Tab 
                disableRipple
                label="Wszystkie" 
                sx={{
                  textTransform: 'none',
                  color: '#020242',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  px: 3,
                  minHeight: 32,
                  height: 32,
                  mt: '8px',
                  bgcolor: '#F1F1F6',
                  borderRadius: '4px 4px 0 0',
                  '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                  '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                }}
              />
              <Tab 
                disableRipple
                label="Oczekujące" 
                sx={{
                  textTransform: 'none',
                  color: '#020242',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  px: 3,
                  minHeight: 32,
                  height: 32,
                  mt: '8px',
                  bgcolor: '#F1F1F6',
                  borderRadius: '4px 4px 0 0',
                  '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                  '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                }}
              />
              <Tab 
                disableRipple
                label="Podpisane" 
                sx={{
                  textTransform: 'none',
                  color: '#020242',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  px: 3,
                  minHeight: 32,
                  height: 32,
                  mt: '8px',
                  bgcolor: '#F1F1F6',
                  borderRadius: '4px 4px 0 0',
                  '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                  '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                }}
              />
            </Tabs>
          </Box>

          {internalTabs.map((_, idx) => (
            <TabPanel key={idx} value={internalTab} index={idx}>
              <TableContainer 
                component={Paper} 
                sx={{ 
                  height: 432, 
                  overflow: 'auto', 
                  position: 'relative', 
                  border: '1px solid #d0d0d6', 
                  borderBottom: '1px solid #d0d0d6', 
                  borderRadius: 0, 
                  borderTopRightRadius: 4, 
                  borderBottomLeftRadius: 4, 
                  borderBottomRightRadius: 4, 
                  overflowX: 'hidden', 
                  overflowY: 'auto', 
                  boxShadow: 'none', 
                  scrollbarWidth: 'none', 
                  msOverflowStyle: 'none', 
                  '&::-webkit-scrollbar': { width: 0, height: 0 } 
                }}
              >
                <Table 
                  size="small" 
                  stickyHeader 
                  sx={{
                    tableLayout: 'fixed',
                    width: '100%',
                    '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
                    '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
                    '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
                    '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {getVisibleColumns().map((key) => {
                        const label = COLUMN_LABELS_INTERNAL[key];
                        if (!label) return null;
                        if (key === 'actions') {
                          return renderResizableTh(key, <></>);
                        }
                        return renderResizableTh(key, label);
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={getVisibleColumns().length} sx={{ textAlign: 'center', py: 4, color: '#9e9e9e' }}>
                        Brak danych
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>
          ))}
        </>
      )}

      {/* Zakładki i tabela - dla zakładki "Kontrakty budowlane" */}
      {tab === 2 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
            <Tabs 
              value={contractsTab} 
              onChange={(_, v) => setContractsTab(v)} 
              aria-label="contracts tabs"
              sx={{ 
                minHeight: 40,
                height: 40,
                flexGrow: 1,
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTabs-flexContainer': { gap: '2px' }
              }}
            >
              <Tab 
                disableRipple
                label="Umowy z zamawiającym" 
                sx={{
                  textTransform: 'none',
                  color: '#020242',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  px: 3,
                  minHeight: 32,
                  height: 32,
                  mt: '8px',
                  bgcolor: '#F1F1F6',
                  borderRadius: '4px 4px 0 0',
                  '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                  '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                }}
              />
              <Tab 
                disableRipple
                label="Umowy z podwykonawcami" 
                sx={{
                  textTransform: 'none',
                  color: '#020242',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  px: 3,
                  minHeight: 32,
                  height: 32,
                  mt: '8px',
                  bgcolor: '#F1F1F6',
                  borderRadius: '4px 4px 0 0',
                  '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                  '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                }}
              />
            </Tabs>
            <IconButton
              size="small"
              sx={{
                width: 32,
                height: 32,
                borderRadius: '4px',
                mt: '4px',
                p: 0,
                alignSelf: 'flex-end',
                bgcolor: colsOpen && tab === 2 ? '#E0E0F5' : 'transparent', // Kontrakty budowlane
                transition: 'background-color 0.15s ease',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
              }}
              onClick={(e) => {
                const cols = getCurrentColumns();
                if (cols.draftVisible == null) cols.setDraftVisible({ ...cols.visible });
                if (cols.draftOrder == null) cols.setDraftOrder([...cols.order]);
                setColResizeMode(true);
                setColsAnchorEl(e.currentTarget);
              }}
            >
              <Box
                component="img"
                alt="kolumny"
                src={'/assets/icons/frame-344.svg?v=2'}
                sx={{ width: 32, height: 32 }}
              />
            </IconButton>
          </Box>

          <TabPanel value={contractsTab} index={0}>
            <TableContainer 
              component={Paper} 
              sx={{ 
                height: 432, 
                overflow: 'auto', 
                position: 'relative', 
                border: '1px solid #d0d0d6', 
                borderBottom: '1px solid #d0d0d6', 
                borderRadius: 0, 
                borderTopRightRadius: 4, 
                borderBottomLeftRadius: 4, 
                borderBottomRightRadius: 4, 
                overflowX: 'hidden', 
                overflowY: 'auto', 
                boxShadow: 'none', 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none', 
                '&::-webkit-scrollbar': { width: 0, height: 0 } 
              }}
            >
              <Table 
                size="small" 
                stickyHeader 
                sx={{
                  tableLayout: 'fixed',
                  width: '100%',
                  '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
                  '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
                  '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
                  '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
                }}
              >
                <TableHead>
                  <TableRow>
                    {getVisibleColumns().map((key) => {
                      const label = COLUMN_LABELS_CLIENT[key];
                      if (!label) return null;
                      if (key === 'actions') {
                        return renderResizableTh(key, <></>);
                      }
                      return renderResizableTh(key, label);
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={getVisibleColumns().length} sx={{ textAlign: 'center', py: 4, color: '#9e9e9e' }}>
                      Brak danych
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={contractsTab} index={1}>
            <TableContainer 
              component={Paper} 
              sx={{ 
                height: 432, 
                overflow: 'auto', 
                position: 'relative', 
                border: '1px solid #d0d0d6', 
                borderBottom: '1px solid #d0d0d6', 
                borderRadius: 0, 
                borderTopRightRadius: 4, 
                borderBottomLeftRadius: 4, 
                borderBottomRightRadius: 4, 
                overflowX: 'hidden', 
                overflowY: 'auto', 
                boxShadow: 'none', 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none', 
                '&::-webkit-scrollbar': { width: 0, height: 0 } 
              }}
            >
              <Table 
                size="small" 
                stickyHeader 
                sx={{
                  tableLayout: 'fixed',
                  width: '100%',
                  '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
                  '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
                  '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
                  '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
                }}
              >
                <TableHead>
                  <TableRow>
                    {getVisibleColumns().map((key) => {
                      const label = COLUMN_LABELS_SUBCONTRACTOR[key];
                      if (!label) return null;
                      return renderResizableTh(key, label);
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={getVisibleColumns().length} sx={{ textAlign: 'center', py: 4, color: '#9e9e9e' }}>
                      Brak danych
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Okno "Dostosuj kolumny" dla kontraktów budowlanych */}
          <Backdrop 
            open={colsOpen && tab === 2} // Kontrakty budowlane 
            sx={{ 
              color: '#fff', 
              zIndex: (theme) => theme.zIndex.modal + 1, 
              backgroundColor: 'rgba(0,0,0,0.35)', 
              pointerEvents: 'none' 
            }}
          >
            <Paper
              elevation={8}
              sx={{
                position: 'fixed',
                bottom: colsPosition.top === 0 ? 0 : 'auto',
                top: colsPosition.top > 0 ? colsPosition.top : 'auto',
                right: colsPosition.left === 0 ? 0 : 'auto',
                left: colsPosition.left > 0 ? colsPosition.left : 'auto',
                height: '50vh',
                width: '280px',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
                borderRadius: 0,
                zIndex: (theme) => theme.zIndex.modal + 2,
                backgroundColor: '#fff',
                pointerEvents: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box
                sx={{ 
                  px: 2, 
                  py: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  flex: '0 0 auto', 
                  position: 'sticky', 
                  top: 0, 
                  bgcolor: '#FFFFFF', 
                  zIndex: 1, 
                  cursor: 'move', 
                  userSelect: 'none' 
                }}
                onMouseDown={(e) => {
                  const cols = getCurrentColumns();
                  if (cols.draftVisible == null) cols.setDraftVisible({ ...cols.visible });
                  if (cols.draftOrder == null) cols.setDraftOrder([...cols.order]);
                  setColsDragging({ 
                    startX: e.clientX, 
                    startY: e.clientY, 
                    top: colsPosition.top, 
                    left: colsPosition.left 
                  });
                }}
              >
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>
                  Dostosuj kolumny
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => {
                    const cols = getCurrentColumns();
                    setColsAnchorEl(null);
                    setColResizeMode(false);
                    cols.setDraftVisible(null);
                    cols.setDraftOrder(null);
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
              <Divider />
              <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
                {(() => {
                  const cols = getCurrentColumns();
                  const list = cols.draftVisible ?? cols.visible;
                  if (cols.draftVisible == null) cols.setDraftVisible({ ...cols.visible });
                  const ord = cols.draftOrder ?? cols.order;
                  if (cols.draftOrder == null) cols.setDraftOrder([...cols.order]);
                  return ord.map((key) => (
                    <Box 
                      key={key} 
                      sx={{ py: 1, px: 2 }}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData('text/plain', key); }}
                      onDragOver={(e) => { e.preventDefault(); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const src = e.dataTransfer.getData('text/plain');
                        if (!src || src === key) return;
                        cols.setDraftOrder(prev => {
                          const cur = [...(prev || ord)];
                          const from = cur.indexOf(src);
                          const to = cur.indexOf(key);
                          if (from < 0 || to < 0) return cur;
                          cur.splice(to, 0, cur.splice(from, 1)[0]);
                          return cur;
                        });
                      }}
                    >
                      <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <IconButton 
                            onClick={() => cols.setDraftVisible(prev => ({ ...(prev || {}), [key]: !((prev || list)[key] !== false) }))} 
                            size="small" 
                            sx={{ p: 0 }} 
                            aria-label="toggle column"
                          >
                            <Box 
                              component="img" 
                              src={(cols.draftVisible ?? cols.visible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} 
                              alt="check" 
                              sx={{ width: 23, height: 24 }} 
                            />
                          </IconButton>
                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>
                            {cols.labels[key] || key.toUpperCase()}
                          </Typography>
                        </Box>
                        <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                      </Box>
                    </Box>
                  ));
                })()}
              </Box>
              <Divider />
              <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
                <Button 
                  variant="text" 
                  onClick={() => {
                    const cols = getCurrentColumns();
                    setColsAnchorEl(null);
                    setColResizeMode(false);
                    cols.setDraftVisible(null);
                    cols.setDraftOrder(null);
                  }} 
                  sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}
                >
                  Anuluj
                </Button>
                <Button 
                  variant="contained" 
                  sx={{ 
                    width: 123, 
                    height: 40, 
                    borderRadius: '4px', 
                    textTransform: 'none', 
                    fontFamily: 'Roboto, sans-serif', 
                    fontWeight: 600, 
                    fontSize: 14, 
                    backgroundColor: '#020242', 
                    '&:hover': { backgroundColor: '#020242' } 
                  }} 
                  onClick={() => {
                    const cols = getCurrentColumns();
                    if (cols.draftVisible) {
                      cols.setVisible(cols.draftVisible);
                      try {
                        const payload = JSON.stringify(cols.draftVisible);
                        localStorage.setItem(cols.visibleKey, payload);
                      } catch {}
                    }
                    if (cols.draftOrder) {
                      const normalized = cols.draftOrder
                        .filter((k) => cols.all.includes(k))
                        .concat(cols.all.filter(k => !cols.draftOrder!.includes(k)));
                      cols.setOrder(normalized);
                      try {
                        const payload = JSON.stringify(normalized);
                        localStorage.setItem(cols.orderKey, payload);
                      } catch {}
                    }
                    setColsAnchorEl(null);
                    setColResizeMode(false);
                    cols.setDraftVisible(null);
                    cols.setDraftOrder(null);
                  }}
                >
                  Zapisz zmiany
                </Button>
              </Box>
            </Paper>
          </Backdrop>
        </>
      )}

      {/* Zakładki i tabela - dla zakładki "Kontrakty budowlane" */}
      {tab === 2 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
            <Tabs 
              value={contractsTab} 
              onChange={(_, v) => setContractsTab(v)} 
              aria-label="contracts tabs"
              sx={{ 
                minHeight: 40,
                height: 40,
                flexGrow: 1,
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTabs-flexContainer': { gap: '2px' }
              }}
            >
              <Tab 
                disableRipple
                label="Umowy z zamawiającym" 
                sx={{
                  textTransform: 'none',
                  color: '#020242',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  px: 3,
                  minHeight: 32,
                  height: 32,
                  mt: '8px',
                  bgcolor: '#F1F1F6',
                  borderRadius: '4px 4px 0 0',
                  '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                  '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                }}
              />
              <Tab 
                disableRipple
                label="Umowy z podwykonawcami" 
                sx={{
                  textTransform: 'none',
                  color: '#020242',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  px: 3,
                  minHeight: 32,
                  height: 32,
                  mt: '8px',
                  bgcolor: '#F1F1F6',
                  borderRadius: '4px 4px 0 0',
                  '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                  '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                }}
              />
            </Tabs>
            <IconButton
              size="small"
              sx={{
                width: 32,
                height: 32,
                borderRadius: '4px',
                mt: '4px',
                p: 0,
                alignSelf: 'flex-end',
                bgcolor: colsOpen && tab === 2 ? '#E0E0F5' : 'transparent', // Kontrakty budowlane
                transition: 'background-color 0.15s ease',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
              }}
              onClick={(e) => {
                const cols = getCurrentColumns();
                if (cols.draftVisible == null) cols.setDraftVisible({ ...cols.visible });
                if (cols.draftOrder == null) cols.setDraftOrder([...cols.order]);
                setColResizeMode(true);
                setColsAnchorEl(e.currentTarget);
              }}
            >
              <Box
                component="img"
                alt="kolumny"
                src={'/assets/icons/frame-344.svg?v=2'}
                sx={{ width: 32, height: 32 }}
              />
            </IconButton>
          </Box>

          <TabPanel value={contractsTab} index={0}>
            <TableContainer 
              component={Paper} 
              sx={{ 
                height: 432, 
                overflow: 'auto', 
                position: 'relative', 
                border: '1px solid #d0d0d6', 
                borderBottom: '1px solid #d0d0d6', 
                borderRadius: 0, 
                borderTopRightRadius: 4, 
                borderBottomLeftRadius: 4, 
                borderBottomRightRadius: 4, 
                overflowX: 'hidden', 
                overflowY: 'auto', 
                boxShadow: 'none', 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none', 
                '&::-webkit-scrollbar': { width: 0, height: 0 } 
              }}
            >
              <Table 
                size="small" 
                stickyHeader 
                sx={{
                  tableLayout: 'fixed',
                  width: '100%',
                  '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
                  '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
                  '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
                  '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
                }}
              >
                <TableHead>
                  <TableRow>
                    {getVisibleColumns().map((key) => {
                      const label = COLUMN_LABELS_CLIENT[key];
                      if (!label) return null;
                      if (key === 'actions') {
                        return renderResizableTh(key, <></>);
                      }
                      return renderResizableTh(key, label);
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={getVisibleColumns().length} sx={{ textAlign: 'center', py: 4, color: '#9e9e9e' }}>
                      Brak danych
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={contractsTab} index={1}>
            <TableContainer 
              component={Paper} 
              sx={{ 
                height: 432, 
                overflow: 'auto', 
                position: 'relative', 
                border: '1px solid #d0d0d6', 
                borderBottom: '1px solid #d0d0d6', 
                borderRadius: 0, 
                borderTopRightRadius: 4, 
                borderBottomLeftRadius: 4, 
                borderBottomRightRadius: 4, 
                overflowX: 'hidden', 
                overflowY: 'auto', 
                boxShadow: 'none', 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none', 
                '&::-webkit-scrollbar': { width: 0, height: 0 } 
              }}
            >
              <Table 
                size="small" 
                stickyHeader 
                sx={{
                  tableLayout: 'fixed',
                  width: '100%',
                  '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
                  '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
                  '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
                  '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
                }}
              >
                <TableHead>
                  <TableRow>
                    {getVisibleColumns().map((key) => {
                      const label = COLUMN_LABELS_SUBCONTRACTOR[key];
                      if (!label) return null;
                      return renderResizableTh(key, label);
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={getVisibleColumns().length} sx={{ textAlign: 'center', py: 4, color: '#9e9e9e' }}>
                      Brak danych
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Okno "Dostosuj kolumny" dla kontraktów budowlanych */}
          <Backdrop 
            open={colsOpen && tab === 2} // Kontrakty budowlane 
            sx={{ 
              color: '#fff', 
              zIndex: (theme) => theme.zIndex.modal + 1, 
              backgroundColor: 'rgba(0,0,0,0.35)', 
              pointerEvents: 'none' 
            }}
          >
            <Paper
              elevation={8}
              sx={{
                position: 'fixed',
                bottom: colsPosition.top === 0 ? 0 : 'auto',
                top: colsPosition.top > 0 ? colsPosition.top : 'auto',
                right: colsPosition.left === 0 ? 0 : 'auto',
                left: colsPosition.left > 0 ? colsPosition.left : 'auto',
                height: '50vh',
                width: '280px',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
                borderRadius: 0,
                zIndex: (theme) => theme.zIndex.modal + 2,
                backgroundColor: '#fff',
                pointerEvents: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box
                sx={{ 
                  px: 2, 
                  py: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  flex: '0 0 auto', 
                  position: 'sticky', 
                  top: 0, 
                  bgcolor: '#FFFFFF', 
                  zIndex: 1, 
                  cursor: 'move', 
                  userSelect: 'none' 
                }}
                onMouseDown={(e) => {
                  const cols = getCurrentColumns();
                  if (cols.draftVisible == null) cols.setDraftVisible({ ...cols.visible });
                  if (cols.draftOrder == null) cols.setDraftOrder([...cols.order]);
                  setColsDragging({ 
                    startX: e.clientX, 
                    startY: e.clientY, 
                    top: colsPosition.top, 
                    left: colsPosition.left 
                  });
                }}
              >
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>
                  Dostosuj kolumny
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => {
                    const cols = getCurrentColumns();
                    setColsAnchorEl(null);
                    setColResizeMode(false);
                    cols.setDraftVisible(null);
                    cols.setDraftOrder(null);
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
              <Divider />
              <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
                {(() => {
                  const cols = getCurrentColumns();
                  const list = cols.draftVisible ?? cols.visible;
                  if (cols.draftVisible == null) cols.setDraftVisible({ ...cols.visible });
                  const ord = cols.draftOrder ?? cols.order;
                  if (cols.draftOrder == null) cols.setDraftOrder([...cols.order]);
                  return ord.map((key) => (
                    <Box 
                      key={key} 
                      sx={{ py: 1, px: 2 }}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData('text/plain', key); }}
                      onDragOver={(e) => { e.preventDefault(); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const src = e.dataTransfer.getData('text/plain');
                        if (!src || src === key) return;
                        cols.setDraftOrder(prev => {
                          const cur = [...(prev || ord)];
                          const from = cur.indexOf(src);
                          const to = cur.indexOf(key);
                          if (from < 0 || to < 0) return cur;
                          cur.splice(to, 0, cur.splice(from, 1)[0]);
                          return cur;
                        });
                      }}
                    >
                      <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <IconButton 
                            onClick={() => cols.setDraftVisible(prev => ({ ...(prev || {}), [key]: !((prev || list)[key] !== false) }))} 
                            size="small" 
                            sx={{ p: 0 }} 
                            aria-label="toggle column"
                          >
                            <Box 
                              component="img" 
                              src={(cols.draftVisible ?? cols.visible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} 
                              alt="check" 
                              sx={{ width: 23, height: 24 }} 
                            />
                          </IconButton>
                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>
                            {cols.labels[key] || key.toUpperCase()}
                          </Typography>
                        </Box>
                        <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                      </Box>
                    </Box>
                  ));
                })()}
              </Box>
              <Divider />
              <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
                <Button 
                  variant="text" 
                  onClick={() => {
                    const cols = getCurrentColumns();
                    setColsAnchorEl(null);
                    setColResizeMode(false);
                    cols.setDraftVisible(null);
                    cols.setDraftOrder(null);
                  }} 
                  sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}
                >
                  Anuluj
                </Button>
                <Button 
                  variant="contained" 
                  sx={{ 
                    width: 123, 
                    height: 40, 
                    borderRadius: '4px', 
                    textTransform: 'none', 
                    fontFamily: 'Roboto, sans-serif', 
                    fontWeight: 600, 
                    fontSize: 14, 
                    backgroundColor: '#020242', 
                    '&:hover': { backgroundColor: '#020242' } 
                  }} 
                  onClick={() => {
                    const cols = getCurrentColumns();
                    if (cols.draftVisible) {
                      cols.setVisible(cols.draftVisible);
                      try {
                        const payload = JSON.stringify(cols.draftVisible);
                        localStorage.setItem(cols.visibleKey, payload);
                      } catch {}
                    }
                    if (cols.draftOrder) {
                      const normalized = cols.draftOrder
                        .filter((k) => cols.all.includes(k))
                        .concat(cols.all.filter(k => !cols.draftOrder!.includes(k)));
                      cols.setOrder(normalized);
                      try {
                        const payload = JSON.stringify(normalized);
                        localStorage.setItem(cols.orderKey, payload);
                      } catch {}
                    }
                    setColsAnchorEl(null);
                    setColResizeMode(false);
                    cols.setDraftVisible(null);
                    cols.setDraftOrder(null);
                  }}
                >
                  Zapisz zmiany
                </Button>
              </Box>
            </Paper>
          </Backdrop>
        </>
      )}

      {/* Zakładka "Gospodarka własna" */}
      {tab === 3 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
            <Tabs 
              value={internalTab} 
              onChange={(_, v) => setInternalTab(v)} 
              aria-label="internal contracts tabs"
              variant="scrollable"
              scrollButtons="auto"
              sx={{ 
                minHeight: 40,
                height: 40,
                flexGrow: 1,
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTabs-flexContainer': { gap: '2px' }
              }}
            >
              {internalTabs.map((label, idx) => (
                <Tab 
                  key={label}
                  disableRipple
                  label={label}
                  sx={{
                    textTransform: 'none',
                    color: '#020242',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    px: 3,
                    minHeight: 32,
                    height: 32,
                    mt: '8px',
                    bgcolor: '#F1F1F6',
                    borderRadius: '4px 4px 0 0',
                    '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                    '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
                  }}
                />
              ))}
            </Tabs>
            <IconButton
              size="small"
              sx={{
                width: 32,
                height: 32,
                borderRadius: '4px',
                mt: '4px',
                p: 0,
                alignSelf: 'flex-end',
                bgcolor: colsOpen && tab === 3 ? '#E0E0F5' : 'transparent', // Gospodarka własna
                transition: 'background-color 0.15s ease',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
              }}
              onClick={(e) => {
                const cols = getCurrentColumns();
                if (cols.draftVisible == null) cols.setDraftVisible({ ...cols.visible });
                if (cols.draftOrder == null) cols.setDraftOrder([...cols.order]);
                setColResizeMode(true);
                setColsAnchorEl(e.currentTarget);
              }}
            >
              <Box
                component="img"
                alt="kolumny"
                src={'/assets/icons/frame-344.svg?v=2'}
                sx={{ width: 32, height: 32 }}
              />
            </IconButton>
          </Box>

          {internalTabs.map((_, idx) => (
            <TabPanel key={idx} value={internalTab} index={idx}>
            <TableContainer 
              component={Paper} 
              sx={{ 
                height: 432, 
                overflow: 'auto', 
                position: 'relative', 
                border: '1px solid #d0d0d6', 
                borderBottom: '1px solid #d0d0d6', 
                borderRadius: 0, 
                borderTopRightRadius: 4, 
                borderBottomLeftRadius: 4, 
                borderBottomRightRadius: 4, 
                overflowX: 'hidden', 
                overflowY: 'auto', 
                boxShadow: 'none', 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none', 
                '&::-webkit-scrollbar': { width: 0, height: 0 } 
              }}
            >
              <Table 
                size="small" 
                stickyHeader 
                sx={{
                  tableLayout: 'fixed',
                  width: '100%',
                  '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0, fontFamily: 'Roboto, sans-serif' },
                  '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
                  '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0, color: '#2D2D32', fontFamily: 'Roboto, sans-serif' },
                  '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
                }}
              >
                <TableHead>
                  <TableRow>
                    {getVisibleColumns().map((key) => {
                      const label = COLUMN_LABELS_PENDING_SIGNATURE[key];
                      if (!label) return null;
                      if (key === 'actions') {
                        return renderResizableTh(key, <></>);
                      }
                      return renderResizableTh(key, label);
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingContracts ? (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4, color: '#9e9e9e' }}>
                        Ładowanie...
                      </TableCell>
                    </TableRow>
                  ) : contractsPendingSignature.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4, color: '#9e9e9e' }}>
                        Brak danych
                      </TableCell>
                    </TableRow>
                  ) : (
                    contractsPendingSignature.map((contract) => (
                      <TableRow key={contract.id}>
                        <TableCell>{contract.contract_number}</TableCell>
                        <TableCell>{contract.contract_date}</TableCell>
                        <TableCell>{contract.employee_name}</TableCell>
                        <TableCell>{contract.description}</TableCell>
                        <TableCell>{contract.employee_email}</TableCell>
                        <TableCell>{contract.unitresco_name}</TableCell>
                        <TableCell>{contract.unitresco_email}</TableCell>
                        <TableCell>{contract.status}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <IconButton 
                              size="small" 
                              onClick={async () => {
                                try {
                                  const result = await contractPendingSignatureService.generatePdf(contract.id);
                                  window.open(result.pdfUrl, '_blank');
                                  showNotification('PDF został wygenerowany', 'success');
                                } catch (error: any) {
                                  showNotification('Błąd generowania PDF: ' + (error.response?.data?.error || error.message), 'error');
                                }
                              }}
                              title="Generuj PDF"
                            >
                              <Box component="img" src="/assets/icons/picture_as_pdf_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg" alt="PDF" sx={{ width: 20, height: 20 }} />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={async () => {
                                try {
                                  await contractPendingSignatureService.sendSignatureLinks(contract.id);
                                  showNotification('Emaile zostały wysłane', 'success');
                                  loadContractsPendingSignature();
                                } catch (error: any) {
                                  showNotification('Błąd wysyłania emaili: ' + (error.response?.data?.error || error.message), 'error');
                                }
                              }}
                              disabled={contract.status !== 'Oczekujące'}
                              title="Wyślij linki do podpisu"
                            >
                              <Box component="img" src="/assets/icons/send_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg" alt="Wyślij" sx={{ width: 20, height: 20 }} />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                setMenuAnchorEl(e.currentTarget);
                                setSelectedRowId(contract.id.toString());
                              }}
                              title="Więcej opcji"
                            >
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          ))}

          {/* Okno "Dostosuj kolumny" dla gospodarki własnej */}
          <Backdrop 
            open={colsOpen && tab === 3} // Gospodarka własna
            sx={{ 
              color: '#fff', 
              zIndex: (theme) => theme.zIndex.modal + 1, 
              backgroundColor: 'rgba(0,0,0,0.35)', 
              pointerEvents: 'none' 
            }}
          >
            <Paper
              elevation={8}
              sx={{
                position: 'fixed',
                bottom: colsPosition.top === 0 ? 0 : 'auto',
                top: colsPosition.top > 0 ? colsPosition.top : 'auto',
                right: colsPosition.left === 0 ? 0 : 'auto',
                left: colsPosition.left > 0 ? colsPosition.left : 'auto',
                height: '50vh',
                width: '280px',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
                borderRadius: 0,
                zIndex: (theme) => theme.zIndex.modal + 2,
                backgroundColor: '#fff',
                pointerEvents: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box
                sx={{ 
                  px: 2, 
                  py: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  flex: '0 0 auto', 
                  position: 'sticky', 
                  top: 0, 
                  bgcolor: '#FFFFFF', 
                  zIndex: 1, 
                  cursor: 'move', 
                  userSelect: 'none' 
                }}
                onMouseDown={(e) => {
                  const cols = getCurrentColumns();
                  if (cols.draftVisible == null) cols.setDraftVisible({ ...cols.visible });
                  if (cols.draftOrder == null) cols.setDraftOrder([...cols.order]);
                  setColsDragging({ 
                    startX: e.clientX, 
                    startY: e.clientY, 
                    top: colsPosition.top, 
                    left: colsPosition.left 
                  });
                }}
              >
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>
                  Dostosuj kolumny
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => {
                    const cols = getCurrentColumns();
                    setColsAnchorEl(null);
                    setColResizeMode(false);
                    cols.setDraftVisible(null);
                    cols.setDraftOrder(null);
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
              <Divider />
              <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
                {(() => {
                  const cols = getCurrentColumns();
                  const list = cols.draftVisible ?? cols.visible;
                  if (cols.draftVisible == null) cols.setDraftVisible({ ...cols.visible });
                  const ord = cols.draftOrder ?? cols.order;
                  if (cols.draftOrder == null) cols.setDraftOrder([...cols.order]);
                  return ord.map((key) => (
                    <Box 
                      key={key} 
                      sx={{ py: 1, px: 2 }}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData('text/plain', key); }}
                      onDragOver={(e) => { e.preventDefault(); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const src = e.dataTransfer.getData('text/plain');
                        if (!src || src === key) return;
                        cols.setDraftOrder(prev => {
                          const cur = [...(prev || ord)];
                          const from = cur.indexOf(src);
                          const to = cur.indexOf(key);
                          if (from < 0 || to < 0) return cur;
                          cur.splice(to, 0, cur.splice(from, 1)[0]);
                          return cur;
                        });
                      }}
                    >
                      <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <IconButton 
                            onClick={() => cols.setDraftVisible(prev => ({ ...(prev || {}), [key]: !((prev || list)[key] !== false) }))} 
                            size="small" 
                            sx={{ p: 0 }} 
                            aria-label="toggle column"
                          >
                            <Box 
                              component="img" 
                              src={(cols.draftVisible ?? cols.visible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} 
                              alt="check" 
                              sx={{ width: 23, height: 24 }} 
                            />
                          </IconButton>
                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>
                            {cols.labels[key] || key.toUpperCase()}
                          </Typography>
                        </Box>
                        <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                      </Box>
                    </Box>
                  ));
                })()}
              </Box>
              <Divider />
              <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
                <Button 
                  variant="text" 
                  onClick={() => {
                    const cols = getCurrentColumns();
                    setColsAnchorEl(null);
                    setColResizeMode(false);
                    cols.setDraftVisible(null);
                    cols.setDraftOrder(null);
                  }} 
                  sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}
                >
                  Anuluj
                </Button>
                <Button 
                  variant="contained" 
                  sx={{ 
                    width: 123, 
                    height: 40, 
                    borderRadius: '4px', 
                    textTransform: 'none', 
                    fontFamily: 'Roboto, sans-serif', 
                    fontWeight: 600, 
                    fontSize: 14, 
                    backgroundColor: '#020242', 
                    '&:hover': { backgroundColor: '#020242' } 
                  }} 
                  onClick={() => {
                    const cols = getCurrentColumns();
                    if (cols.draftVisible) {
                      cols.setVisible(cols.draftVisible);
                      try {
                        const payload = JSON.stringify(cols.draftVisible);
                        localStorage.setItem(cols.visibleKey, payload);
                      } catch {}
                    }
                    if (cols.draftOrder) {
                      const normalized = cols.draftOrder
                        .filter((k) => cols.all.includes(k))
                        .concat(cols.all.filter(k => !cols.draftOrder!.includes(k)));
                      cols.setOrder(normalized);
                      try {
                        const payload = JSON.stringify(normalized);
                        localStorage.setItem(cols.orderKey, payload);
                      } catch {}
                    }
                    setColsAnchorEl(null);
                    setColResizeMode(false);
                    cols.setDraftVisible(null);
                    cols.setDraftOrder(null);
                  }}
                >
                  Zapisz zmiany
                </Button>
              </Box>
            </Paper>
          </Backdrop>
        </>
      )}

      {/* Menu dropdown dla akcji - Umowy do podpisu */}
      {tab === 1 && (
        <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={() => {
              setMenuAnchorEl(null);
              setSelectedRowId(null);
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem 
              onClick={async () => {
                if (selectedRowId) {
                  try {
                    await contractPendingSignatureService.deleteContract(parseInt(selectedRowId));
                    await loadContractsPendingSignature();
                    setMenuAnchorEl(null);
                    setSelectedRowId(null);
                    showNotification('Umowa została usunięta', 'success');
                  } catch (error: any) {
                    showNotification('Błąd usuwania umowy: ' + (error.response?.data?.error || error.message), 'error');
                  }
                }
              }}
              sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px' }}
            >
              Usuń
            </MenuItem>
          </Menu>
      )}

      {/* Menu dropdown dla akcji - Wzory umów */}
      {tab === 0 && (
        <Menu
            anchorEl={templateMenuAnchorEl}
            open={Boolean(templateMenuAnchorEl)}
            onClose={() => {
              setTemplateMenuAnchorEl(null);
              setSelectedTemplateId(null);
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem 
              onClick={() => {
                if (selectedTemplateId) {
                  const template = contractTemplates.find(t => t.id === selectedTemplateId);
                  if (template) {
                    setEditingTemplate(template);
                    setEditTemplateOpen(true);
                  }
                  setTemplateMenuAnchorEl(null);
                  setSelectedTemplateId(null);
                }
              }}
              sx={{ 
                fontFamily: 'Roboto, sans-serif', 
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box component="img" src="/assets/icons/edit2.svg" alt="Edytuj" sx={{ width: 18, height: 18 }} />
              Edytuj
            </MenuItem>
            <MenuItem 
              onClick={async () => {
                if (selectedTemplateId) {
                  const template = contractTemplates.find(t => t.id === selectedTemplateId);
                  if (template) {
                    try {
                      // Znajdź najwyższą wersję dla tego wzoru (tej samej nazwy)
                      const sameNameTemplates = contractTemplates.filter(t => t.name === template.name);
                      let maxVersion = 0;
                      sameNameTemplates.forEach(t => {
                        const versionNum = parseInt(t.version || '0', 10);
                        if (!isNaN(versionNum) && versionNum > maxVersion) {
                          maxVersion = versionNum;
                        }
                      });
                      const nextVersion = maxVersion + 1;

                      // Skopiuj dokument Google Docs
                      const sourceDocumentId = template.google_doc_url.match(/\/d\/([a-zA-Z0-9-_]+)/)?.[1];
                      if (!sourceDocumentId) {
                        showNotification('Nie można skopiować dokumentu - nieprawidłowy link', 'error');
                        setTemplateMenuAnchorEl(null);
                        setSelectedTemplateId(null);
                        return;
                      }

                      // Utwórz kopię dokumentu - użyj createTemplate z linkiem źródłowym
                      // Backend automatycznie skopiuje dokument i doda wersję do stopki
                      const copyResult = await contractTemplateService.createTemplate({
                        name: template.name,
                        category: template.category,
                        google_doc_url: template.google_doc_url,
                        description: template.description,
                        version: nextVersion.toString(),
                        status: template.status || 'W opracowaniu'
                      });

                      // Otwórz formularz z wypełnionymi danymi i nową wersją
                      setEditingTemplate({
                        ...copyResult,
                        version: nextVersion.toString()
                      } as ContractTemplate);
                      setEditTemplateOpen(true);
                      await loadContractTemplates();
                    } catch (error: any) {
                      showNotification('Błąd tworzenia kolejnej wersji: ' + (error.response?.data?.error || error.message), 'error');
                    }
                  }
                  setTemplateMenuAnchorEl(null);
                  setSelectedTemplateId(null);
                }
              }}
              sx={{ 
                fontFamily: 'Roboto, sans-serif', 
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box component="img" src="/assets/icons/update_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg" alt="Kolejna wersja" sx={{ width: 18, height: 18 }} />
              Kolejna wersja
            </MenuItem>
          </Menu>
      )}

      {/* Placeholder treści – dla pozostałych zakładek */}
      {tab !== 0 && tab !== 1 && tab !== 2 && tab !== 3 && (
        <Box sx={{ p: 2 }}>
          <Typography>Moduł Umowy w przygotowaniu</Typography>
        </Box>
      )}

      {/* Formularz dodawania umowy */}
      <AddContractForm
        open={addContractOpen}
        onClose={() => setAddContractOpen(false)}
        onAdd={async (contract) => {
          try {
            if (tab === 1) {
              // Umowa do podpisu (nowy index)
              await contractPendingSignatureService.createContract({
                contractNumber: contract.contractNumber,
                contractDate: contract.date,
                description: contract.description || '',
                employeeName: contract.employeeName || '',
                employeeEmail: contract.employeeEmail || '',
                gdocLink: contract.gdocLink || '',
                unitrescoName: contract.unitrescoName || '',
                unitrescoEmail: contract.unitrescoEmail || ''
              });
              await loadContractsPendingSignature();
            } else {
              // TODO: Implementacja dla innych typów umów
              console.log('Dodawanie umowy:', contract);
            }
            setAddContractOpen(false);
            showNotification('Umowa została dodana', 'success');
          } catch (error: any) {
            console.error('Błąd dodawania umowy:', error);
            showNotification('Błąd dodawania umowy: ' + (error.response?.data?.error || error.message), 'error');
          }
        }}
        contractType={
          tab === 0
            ? 'template' // Wzory umów i protokołów
            : tab === 1
            ? 'pendingSignature' // Umowy do podpisu
            : tab === 2
            ? (contractsTab === 0 ? 'client' : 'subcontractor') // Kontrakty budowlane
            : tab === 3
            ? (internalTab === 0 ? 'employment'
            : internalTab === 1 ? 'commission'
            : internalTab === 2 ? 'b2b'
            : internalTab === 3 ? 'rent'
            : internalTab === 4 ? 'leasing'
            : internalTab === 5 ? 'insurance'
            : internalTab === 6 ? 'bank'
            : 'other') // Gospodarka własna
            : 'other'
        }
      />

      {/* Formularz dodawania/edycji wzoru umowy */}
      {editTemplateOpen && (
        <AddTemplateForm
          open={editTemplateOpen}
          onClose={() => {
            setEditTemplateOpen(false);
            setEditingTemplate(null);
          }}
          onSave={async (data) => {
            try {
              if (editingTemplate) {
                await contractTemplateService.updateTemplate(editingTemplate.id, {
                  name: data.name,
                  google_doc_url: data.google_doc_url,
                  description: data.description,
                  version: data.version,
                  status: data.status,
                  category: data.category as any
                });
                showNotification('Wzór umowy został zaktualizowany', 'success');
              } else {
                await contractTemplateService.createTemplate({
                  name: data.name,
                  google_doc_url: data.google_doc_url,
                  description: data.description,
                  version: data.version,
                  status: data.status || 'W opracowaniu',
                  category: data.category as any
                });
                showNotification('Wzór umowy został dodany', 'success');
              }
              await loadContractTemplates();
              setEditTemplateOpen(false);
              setEditingTemplate(null);
            } catch (error: any) {
              showNotification('Błąd zapisywania wzoru umowy: ' + (error.response?.data?.error || error.message), 'error');
            }
          }}
          onDelete={async (id: number) => {
            await contractTemplateService.deleteTemplate(id);
            await loadContractTemplates();
            setEditTemplateOpen(false);
            setEditingTemplate(null);
          }}
          template={editingTemplate}
          category={['subcontracting', 'employment', 'regulations', 'protocols'][templateTab] as any}
        />
      )}
    </Box>
  );
};

// Komponent formularza dodawania/edycji wzoru umowy
interface AddTemplateFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { name: string; google_doc_url: string; description?: string; version?: string; status?: string; category: string }) => void;
  onDelete?: (id: number) => Promise<void>;
  template?: ContractTemplate | null;
  category: 'subcontracting' | 'employment' | 'regulations' | 'protocols';
}

const AddTemplateForm: React.FC<AddTemplateFormProps> = ({ open, onClose, onSave, onDelete, template, category: initialCategory }) => {
  const { showNotification } = useNotification();
  const [selectedCategory, setSelectedCategory] = React.useState<'subcontracting' | 'employment' | 'regulations' | 'protocols' | ''>('');
  const [formData, setFormData] = React.useState({
    name: '',
    google_doc_url: '',
    description: '',
    version: '1',
    status: 'W opracowaniu'
  });
  const [loading, setLoading] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  React.useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        google_doc_url: template.google_doc_url,
        description: template.description || '',
        version: template.version || '',
        status: template.status || 'W opracowaniu'
      });
      setSelectedCategory(template.category);
    } else {
      setFormData({
        name: '',
        google_doc_url: '',
        description: '',
        version: '1',
        status: 'W opracowaniu'
      });
      setSelectedCategory('');
    }
  }, [template, open, initialCategory]);

  // ESC zamyka formularz
  React.useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);


  const handleSubmit = async () => {
    if (!formData.name || !selectedCategory) {
      showNotification('Wypełnij wszystkie wymagane pola', 'error');
      return;
    }
    setLoading(true);
    try {
      await onSave({ ...formData, category: selectedCategory, status: formData.status || 'W opracowaniu' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!template || !onDelete) return;
    
    if (!window.confirm('Czy na pewno chcesz usunąć ten wzór umowy?')) {
      return;
    }

    setDeleting(true);
    try {
      await onDelete(template.id);
      showNotification('Wzór umowy został usunięty', 'success');
      onClose();
    } catch (error: any) {
      showNotification('Błąd usuwania wzoru umowy: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Backdrop open={open} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
      <Paper
        sx={{
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: 680,
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
          bgcolor: '#fff'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
            {template ? 'Edytuj wzór umowy' : 'Dodaj wzór umowy'}
          </Typography>
          <IconButton 
            onClick={onClose} 
            size="small"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 30,
              minHeight: 30,
              '& > *': {
                pointerEvents: 'none',
              },
            }}
          >
            <Box 
              component="img" 
              src="/assets/close.svg" 
              alt="Zamknij" 
              sx={{ 
                width: 13, 
                height: 13, 
                display: 'block',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }} 
            />
          </IconButton>
        </Box>
        <Box sx={{ p: 4, pt: 3, flex: 1, overflowY: 'auto' }}>
          {/* Pole Typ */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, color: '#2D2D32', mb: '4px' }}>
              Typ <span style={{ color: '#D32F2F' }}>*</span>
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedCategory || ''}
                onChange={(e) => !template && setSelectedCategory(e.target.value as any)}
                disabled={!!template}
                displayEmpty
                renderValue={(value) => {
                  if (!value) {
                    return <span style={{ color: '#9e9ea7' }}>Wybierz typ</span>;
                  }
                  const options: Record<string, string> = {
                    subcontracting: 'Umowy o podwykonawstwo',
                    employment: 'Umowy Pracownicze',
                    regulations: 'Regulaminy',
                    protocols: 'Protokoły'
                  };
                  return options[value] || '';
                }}
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  color: '#2D2D32',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#d4d4d9'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#595966'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#595966'
                  },
                  '&.Mui-disabled': {
                    opacity: 0.6,
                    backgroundColor: '#fff'
                  },
                  '& .MuiSelect-select': {
                    padding: '8px 12px',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    color: '#2D2D32'
                  }
                }}
              >
                <MenuItem value="subcontracting">Umowy o podwykonawstwo</MenuItem>
                <MenuItem value="employment">Umowy Pracownicze</MenuItem>
                <MenuItem value="regulations">Regulaminy</MenuItem>
                <MenuItem value="protocols">Protokoły</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, color: '#2D2D32', mb: '4px' }}>
              Nazwa <span style={{ color: '#D32F2F' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="Nazwa wzoru"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  '& fieldset': { borderColor: '#d4d4d9' },
                  '&:hover fieldset': { borderColor: '#595966' },
                  '&.Mui-focused fieldset': { borderColor: '#595966' }
                },
                '& .MuiInputBase-input': {
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  color: '#2D2D32',
                  padding: '8px 12px'
                }
              }}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, color: '#2D2D32', mb: '4px' }}>
              Link do Google Docs <span style={{ color: '#D32F2F' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="Wklej link lub wygeneruje się automatycznie przy zapisie"
              value={formData.google_doc_url}
              onChange={(e) => setFormData({ ...formData, google_doc_url: e.target.value })}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  '& fieldset': { borderColor: '#d4d4d9' },
                  '&:hover fieldset': { borderColor: '#595966' },
                  '&.Mui-focused fieldset': { borderColor: '#595966' }
                },
                '& .MuiInputBase-input': {
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  color: '#2D2D32',
                  padding: '8px 12px'
                }
              }}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, color: '#2D2D32', mb: '4px' }}>
              Opis
            </Typography>
            <TextField
              fullWidth
              placeholder="Opis wzoru (opcjonalnie)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              variant="outlined"
              multiline
              rows={4}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  '& fieldset': { borderColor: '#d4d4d9' },
                  '&:hover fieldset': { borderColor: '#595966' },
                  '&.Mui-focused fieldset': { borderColor: '#595966' }
                },
                '& .MuiInputBase-input': {
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  color: '#2D2D32',
                  padding: '8px 12px'
                }
              }}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, color: '#2D2D32', mb: '4px' }}>
              Wersja
            </Typography>
            <TextField
              fullWidth
              type="number"
              placeholder="Wersja wzoru (opcjonalnie)"
              value={formData.version}
              onChange={(e) => {
                const value = e.target.value;
                // Zezwól na pustą wartość lub liczbę
                if (value === '' || /^\d+$/.test(value)) {
                  setFormData({ ...formData, version: value });
                }
              }}
              inputProps={{ min: 1, step: 1 }}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  '& fieldset': { borderColor: '#d4d4d9' },
                  '&:hover fieldset': { borderColor: '#595966' },
                  '&.Mui-focused fieldset': { borderColor: '#595966' }
                },
                '& .MuiInputBase-input': {
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  color: '#2D2D32',
                  padding: '8px 12px'
                }
              }}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, color: '#2D2D32', mb: '4px' }}>
              Status
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  color: '#2D2D32',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#d4d4d9'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#595966'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#595966'
                  },
                  '& .MuiSelect-select': {
                    padding: '8px 12px',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    color: '#2D2D32'
                  }
                }}
              >
                <MenuItem value="W opracowaniu">W opracowaniu</MenuItem>
                <MenuItem value="Do zatwierdzenia">Do zatwierdzenia</MenuItem>
                <MenuItem value="Uwagi">Uwagi</MenuItem>
                <MenuItem value="Odrzucam">Odrzucam</MenuItem>
                <MenuItem value="Zatwierdzam">Zatwierdzam</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        <Divider />
        <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'space-between', gap: 2, boxShadow: '0px 4px 4px rgba(0,0,0,0.05)' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {template && onDelete && (
              <Button
                onClick={handleDelete}
                variant="text"
                disabled={loading || deleting}
                sx={{ 
                  fontFamily: 'Roboto, sans-serif', 
                  textTransform: 'none',
                  color: '#D32F2F',
                  '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' }
                }}
              >
                {deleting ? <CircularProgress size={24} color="inherit" /> : 'Usuń'}
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={onClose}
              variant="text"
              disabled={loading || deleting}
              sx={{ fontFamily: 'Roboto, sans-serif', textTransform: 'none' }}
            >
              Anuluj
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || deleting}
              sx={{
                backgroundColor: '#020242',
                fontFamily: 'Roboto, sans-serif',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#030360' }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : (template ? 'Zapisz zmiany' : 'Dodaj wzór')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Backdrop>
  );
};

export default ContractsPage;



